import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { metaDesc } from "@/lib/seo-description";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Désinscription e-mail | DJ Val" },
      { name: "description", content: metaDesc("Gérez votre désinscription aux e-mails de DJ Val.") },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Désinscription e-mail — DJ Val" },
      { property: "og:description", content: metaDesc("Gérez votre désinscription aux e-mails de DJ Val.") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

type State = "loading" | "ready" | "done" | "already" | "invalid";

function Page() {
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);
  const token = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("token")
    : null;

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : { valid: false }))
      .then((d) => {
        if (d.valid) setState("ready");
        else if (d.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      setState(d.success ? "done" : d.reason === "already_unsubscribed" ? "already" : "invalid");
    } catch {
      setState("invalid");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-24 bg-background">
      <div className="max-w-md w-full border border-foreground/10 bg-card p-8 text-center">
        <p className="eyebrow text-foreground">DJ Val</p>
        <h1 className="font-serif-display text-3xl text-foreground mt-3">Désinscription</h1>
        <span className="gold-rule mx-auto !w-12 my-6" />
        {state === "loading" && (
          <p className="text-sm text-muted-foreground font-light">Vérification du lien…</p>
        )}
        {state === "ready" && (
          <>
            <p className="text-sm text-muted-foreground font-light">
              Confirmez pour ne plus recevoir d'e-mails de DJ Val.
            </p>
            <button
              onClick={confirm}
              disabled={busy}
              className="mt-6 w-full bg-[var(--gold)] text-foreground px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all disabled:opacity-40"
            >
              {busy ? "En cours…" : "Confirmer la désinscription"}
            </button>
          </>
        )}
        {state === "done" && (
          <p className="text-sm text-foreground font-light">
            C'est fait — vous ne recevrez plus d'e-mails de notre part.
          </p>
        )}
        {state === "already" && (
          <p className="text-sm text-foreground font-light">
            Vous êtes déjà désinscrit.
          </p>
        )}
        {state === "invalid" && (
          <p className="text-sm text-muted-foreground font-light">
            Ce lien de désinscription est invalide ou expiré.
          </p>
        )}
      </div>
    </div>
  );
}
